import { DragDrop } from '@/components/drag-drop';
import {
  CheckCircleIcon,
  ClockIcon,
  LinkIcon,
  PhotoIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Popover from '@radix-ui/react-popover';
import { ObjectPickerForm } from '@/components/object-picker-form';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTestAgent } from '@/features/agents/hooks/use-test-agent';
import { ObjectParams } from '@/hooks/use-relation-insert';
import {
  DefaultValues,
  Step,
} from '@/features/agents/components/capabilities-studio-widget';
import { useEffect, useState } from 'react';
import { Navigation } from '@/features/agents/components/navigation';
import { useEmitter } from '@/hooks/use-emitter';
import { Badge } from '@/components/badge';
import { Timer } from '@/features/agents/components/timer';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { useTheme } from '@/hooks/use-theme';
import { useGetAgentRuntime } from '@/features/agents/hooks/use-get-agent-runtime';
import { useScreenId } from '@/hooks/use-screen-id';
import { useTabId } from '@/hooks/use-tab-id';
import { Output } from '@/features/agents/components/output';
import { Editor } from '@/features/agents/components/editor';
import { WidgetID } from '@/widget-store';
import * as Accordion from '@radix-ui/react-accordion';
import { PromptBuilder } from '@/components/prompt-builder';
import { JsonEditor } from '@/components/json-editor';
import { useObjectJson } from '@/hooks/use-object-json';
import clsx from 'clsx';
import { useRunAgentTest } from '@/features/agents/hooks/use-run-agent-test';
import { usePublishAgent } from '@/features/agents/hooks/use-publish-agent';
import { useInitializeAgent } from '@/features/agents/hooks/use-initialize-agent';
import { Button } from '@/components/button';
import { useRefineAgent } from '@/features/agents/hooks/use-refine-agent';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { useUiPickerObjects } from '@/hooks/ui/use-ui-picker-objects';
import { useAuthorisedFunction } from '@/utilities/authorised-function';
import { useCancelAgentTestRun } from '@/features/agents/hooks/use-cancel-agent-test-run';
import { useEcho } from "@laravel/echo-react";

export type TestFormInputs = {
  sourceExample?: {
    uuid: string;
    container: string;
  };
  inputObject?: string;
  title?: string;
  vaultSchemas?: {
    uuid: string;
    container: string;
    label?: string;
    variableName?: string;
  }[];
  // Runtime status fields for persistence - only the essential ones
  runtimeStatus?: {
    output?: string;
    error?: string;
    log?: string;
  };
};

interface TestFormProps {
  branch: string;
  id: WidgetID;
  defaultValues: DefaultValues['test'];
  steps: Step[];
  onChange: (data: TestFormInputs, step: 'test' | 'tune') => void;
  agent: any; // Change from unknown to any to avoid type issues
  isLoading: boolean;
  onTestRun: (uuid: string | null) => void;
  testRunUuid: string | null;
}

function formatVarName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function TestForm({
  branch,
  id,
  defaultValues,
  steps,
  onChange,
  agent: agentData,
  isLoading: isAgentLoading,
  testRunUuid,
  onTestRun,
}: TestFormProps) {
  const tabId = useTabId();
  const screenId = useScreenId();
  const idPrefix = `${tabId}/${screenId}`;
  const { uuid } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TestFormInputs>({
    defaultValues: {
      ...(defaultValues || {}),
    },
  });
  const [agentRuntimeData, setAgentRuntimeData] = useState<any>(defaultValues?.runtimeStatus || {});
  const channel = testRunUuid ? `agent.run.${testRunUuid}` : null;
  const [agentRunUuid, setAgentRunUuid] = useState<string | null>(testRunUuid || null);
  const { data: agentFinalRuntimeData } = useGetAgentRuntime(uuid!, agentRunUuid);

  useEcho(
    channel,
    '.AgentRunNotification',
    (e: any) => {
      console.log(e.message);
      setAgentRuntimeData(e.message.data.runtimeStatus);
      if (e.message.data.runtimeStatus.status === 'inactive') {
        setAgentRunUuid(testRunUuid);
      }
    },
    [testRunUuid]
  );

  useEffect(() => {
    if (agentFinalRuntimeData) {
      setAgentRuntimeData(agentFinalRuntimeData);
    }
  }, [agentFinalRuntimeData]);

  // useEffect(() => {
  //   if (!testRunUuid) return;
  //   const eventSource = new EventSource(`${import.meta.env.VITE_IMPORT_ENGINE_OCTANE_ENDPOINT}/api/v1/agent/run/${testRunUuid}/stream`);
  //   eventSource.onmessage = (event) => {
  //     const parsedData = JSON.parse(event.data);
  //     console.log('SSE message received:', parsedData);

  //     return () => eventSource.close();
  //   };
  // }, [testRunUuid]);


  useEffect(() => {
    if (agentRuntimeData) {
      setRuntimeData({
        status: agentRuntimeData?.status || 'inactive',
        attributes: agentRuntimeData?.attributes || {},
        output: agentRuntimeData?.output || defaultValues?.runtimeStatus?.output,
        error: agentRuntimeData?.error || defaultValues?.runtimeStatus?.error,
        log: agentRuntimeData?.log || defaultValues?.runtimeStatus?.log,
      });
    }
  }, [agentRuntimeData, defaultValues]);

  const { theme } = useTheme('light');
  const sourceExample = watch('sourceExample');
  const { data: jsonSourceData } = useObjectJson(
    sourceExample?.uuid || '',
    sourceExample?.container || '',
    {
      refetchInterval: 0,
      onSuccess: (data: any) => {
        // Only update if we still have a valid source example (not deleted)
        if (sourceExample?.uuid && sourceExample?.container) {
          setValue('inputObject', data.data);
          onChange(
            { ...defaultValues, inputObject: data.data },
            'test'
          );
        }
      },
    }
  );
  const [startTime, setStartTime] = useState<Date | null>(null);
  const publishAgentMutation = usePublishAgent();
  const initializeAgentMutation = useInitializeAgent();
  const refineAgentMutation = useRefineAgent();
  const queryClient = useQueryClient();
  const isVaultRefetching = useIsFetching({ queryKey: ['ui-picker-objects', 'vault'] }) > 0;
  const { data: vaultPickerData } = useUiPickerObjects('vault');
  const vaultPickerItems = vaultPickerData?.data?.items || [];
  const [jsonEditorData, setJsonEditorData] = useState<Record<string, unknown>>(
    {}
  );

  // Use persisted runtime data for output, error, log; use live data for status and attributes
  const [runtimeData, setRuntimeData] = useState({
    status: agentRuntimeData?.status || 'inactive',
    attributes: agentRuntimeData?.attributes || {},
    output: agentRuntimeData?.output || defaultValues?.runtimeStatus?.output,
    error: agentRuntimeData?.error || defaultValues?.runtimeStatus?.error,
    log: agentRuntimeData?.log || defaultValues?.runtimeStatus?.log,
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    if (jsonSourceData?.data) {
      setJsonEditorData(JSON.parse(jsonSourceData.data));
    } else if (defaultValues?.inputObject) {
      setJsonEditorData(JSON.parse(defaultValues.inputObject));
    } else {
      setJsonEditorData({});
    }
  }, [jsonSourceData?.data, defaultValues?.inputObject]);

  // Persist only output, error, and log when they change
  useEffect(() => {
    if (agentRuntimeData && testRunUuid) {
      const currentPersistedData = defaultValues?.runtimeStatus || {};
      const newRuntimeData = {
        output: agentRuntimeData.output,
        error: agentRuntimeData.error,
        log: agentRuntimeData.log,
      };

      // Only update if the data has actually changed
      if (
        currentPersistedData.output !== newRuntimeData.output ||
        currentPersistedData.error !== newRuntimeData.error ||
        currentPersistedData.log !== newRuntimeData.log
      ) {
        onChange(
          {
            ...defaultValues,
            runtimeStatus: newRuntimeData,
          },
          'test'
        );
      }
    }
  }, [
    agentRuntimeData?.output,
    agentRuntimeData?.error,
    agentRuntimeData?.log,
    testRunUuid,
    defaultValues?.runtimeStatus,
  ]);

  const { isAuthorised } = useAuthorisedFunction();
  const [openVaultPopover, setOpenVaultPopover] = useState<string | null>(null);

  const options: EChartsOption = {
    color: [
      'rgb(199, 210, 254)',
      'rgb(165, 180, 252)',
      'rgb(129, 140, 248)',
      'rgb(99, 102, 241)',
      'rgb(79, 70, 229)',
      'rgb(67, 56, 202)',
      'rgb(55, 48, 163)',
      'rgb(49, 46, 129)',
    ],
    angleAxis: {
      startAngle: 90,
      splitLine: {
        show: false,
      },
      show: false,
      max: 'dataMax',
    },
    radiusAxis: {
      type: 'category',
      z: 10,
      show: false,
      min: -1,
    },
    polar: {
      radius: '85%',
      center: ['65%', '50%'],
    },
    series: (Array.isArray(runtimeData?.attributes)
      ? runtimeData?.attributes
      : []
    ).map((attribute) => {
      let itemValue = runtimeData?.attributes?.find(
        (item) => item.label === attribute.label
      );
      const updatedName = `${attribute.label}: ${
        itemValue ? itemValue.value : ''
      }`;

      return {
        type: 'bar',
        showBackground: true,
        itemStyle: {
          backgroundColor: 'gray',
        },
        data: [attribute.value as number],
        coordinateSystem: 'polar',
        roundCap: true,
        name: updatedName,
      };
    }),
    legend: {
      show: true,
      orient: 'vertical',
      left: 'left',
      top: '40%',
      selectMode: false,
      textStyle: { color: theme === 'light' ? '#000' : '#fff' },
      formatter: `{name}`,
    },
    tooltip: {
      show: true,
      formatter: '{a}<br /><strong>{c0}</strong>',
    },
  };

  const testAgentMutation = useTestAgent();
  const cancelAgentTestRunMutation = useCancelAgentTestRun();

  useEffect(() => {
    if (runtimeData?.status !== 'running') {
      setStartTime(null);
    }
  }, [runtimeData?.status]);

  useEffect(() => {
    if (
      runtimeData?.output &&
      typeof runtimeData.output === 'object' &&
      'html' in runtimeData.output
    ) {
      emit({
        type: 'preview/html',
        data: [
          {
            html: (runtimeData.output as any).html,
          },
        ],
      });
    }
  }, [runtimeData?.output]);

  const emit = useEmitter({
    id,
    listenersPrefix: idPrefix,
  });

  useEffect(() => {
    emit({
      type: 'preview/html',
      data: {
        html: '',
      },
    });
  }, [uuid]);

  useEffect(() => {
    if (!agentData || typeof agentData !== 'object' || !('code' in agentData)) {
      return;
    }

    emit({
      type: 'source-code/success',
      data: {
        sourceCode: {
          content: (agentData as any).code,
          language: (agentData as any).codeLanguage,
          previousContent: (agentData as any).codePrevious,
        },
        agentUuid: uuid!,
      },
    });
  }, [agentData]);

  function onTest(data: TestFormInputs) {
    setRuntimeData(prev => ({
      ...prev,
      status: 'running',
    }));
    setStartTime(new Date());
    const activeVars = new Set(
      (agentData?.vaultSchemas || [])
        .map((s: { variableName?: string }) => s.variableName)
        .filter(Boolean)
    );
    const configuration = Object.fromEntries(
      (vaultSchemas || [])
        .filter((item) => item.variableName && activeVars.has(item.variableName))
        .map((item) => [item.variableName!, { uuid: item.uuid, container: item.container }])
    );
    testAgentMutation.mutate(
      {
        branch,
        agentUuid2: uuid!,
        sourceExample: data.sourceExample,
        inputObject: data.inputObject,
        configuration: configuration,
      },
      {
        onSuccess: (data) => {
          onTestRun(data.runUuid);
        },
        onError: (error) => {
          setRuntimeData(prev => ({
            ...prev,
            status: 'failed',
            error: error.message,
          }));
        },
      }
    );
  }

  function handleDrop(
    { value }: { value: ObjectParams[] },
    field: keyof TestFormInputs
  ) {
    setValue(field, { uuid: value[0].uuid, container: value[0].container });
    onChange(
      {
        ...defaultValues,
        [field]: { uuid: value[0].uuid, container: value[0].container },
      },
      'test'
    );
  }

  function handleVaultDrop(
    data: Record<string, unknown>,
    variableName: string
  ) {
    const value = data.value as (ObjectParams & { label?: string })[];
    const newItem = { uuid: value[0].uuid, container: value[0].container, label: value[0].label, variableName };
    const newItems = [
      ...(vaultSchemas || []).filter((i) => i.variableName !== variableName),
      newItem,
    ];
    setValue('vaultSchemas', newItems);
    onChange({ ...defaultValues, vaultSchemas: newItems }, 'test');
  }

  const isLoading =
    testAgentMutation.isLoading ||
    runtimeData?.status === 'running' ||
    startTime !== null;

  function handlePublish() {
    publishAgentMutation.mutate(
      {
        uuid: uuid!,
      },
      {
        onSuccess: () => {
          console.log('Agent published successfully');
        },
      }
    );
  }

  function handleInitialize() {
    initializeAgentMutation.mutate({
      uuid: uuid!,
    });
  }

  const title = watch('title');

  function handleAutoCorrect() {
    refineAgentMutation.mutate(
      {
        branch,
        agentUuid: uuid!,
        autoCorrect: true,
        title,
        error: runtimeData?.error,
      },
      {
        onSuccess: (data: any) => {
          queryClient.invalidateQueries({ queryKey: ['agent', uuid] });

          emit({
            type: 'source-code/success',
            data: [
              {
                sourceCode: {
                  content: data.code,
                  language: data.codeLanguage,
                  previousContent: data.codePrevious,
                },
                agentUuid: uuid!,
              },
            ],
          });
        },
      }
    );
  }

  function handleCancel() {
    cancelAgentTestRunMutation.mutateAsync({
      processId: agentRuntimeData.processId!,
      runUuid: testRunUuid!,
    });
  }

  const vaultSchemas = watch('vaultSchemas');

  return (
    <>
      <Navigation
        stepAction={(isLoading) => (
          <div className="flex gap-x-2 items-center">
            {!isLoading ? (
              <button
                form="test-form"
                type="submit"
                className="rounded-md bg-indigo-600 dark:bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 dark:hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Run
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-indigo-600 dark:bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 dark:hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        )}
        loadingStep={isLoading ? 'test' : undefined}
        currentStep="test"
        steps={steps}
      />
      {isAgentLoading ? (
        <div>Loading...</div>
      ) : (
        <div className="p-7 grid gap-6">
          <div>
            <label
              htmlFor="agentTitle"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 sr-only"
            >
              Agent Title
            </label>
            <div className="relative flex items-center gap-4">
              {agentData?.imageUrl ? (
                <img
                  src={agentData?.imageUrl}
                  alt=""
                  className="w-10 h-10 rounded-full flex-none"
                />
              ) : (
                <div className="w-10 h-10 rounded-full flex-none bg-slate-50 dark:bg-gray-800 grid place-items-center">
                  <PhotoIcon className="w-5 h-5 stroke-slate-500 dark:stroke-gray-400" />
                </div>
              )}
              <input
                type="text"
                id="title"
                className="block bg-slate-50 dark:bg-gray-800 w-full rounded-md border-0 py-2.5 px-3 text-gray-900 dark:text-gray-100 shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-2xl sm:leading-6"
                {...register('title', {
                  onChange: (e) => {
                    console.log('__onChange__', e.target.value);

                    onChange(
                      {
                        ...defaultValues,
                        title: e.target.value,
                      },
                      'test'
                    );
                  },
                })}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400 absolute top-1/2 -translate-y-1/2 right-4">
                Rev {agentData?.revision}
              </span>
            </div>
            {errors.title && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                Title is required.
              </p>
            )}
          </div>
          <Accordion.Root
            type="multiple"
            className="space-y-6"
            defaultValue={['test-inputs', 'test-results']}
          >
            <Accordion.Item value="test-inputs">
              <form onSubmit={handleSubmit(onTest)} id="test-form">
                <div className=" p-4 rounded-lg bg-gray-50 dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700/50">
                  <Accordion.Trigger asChild className="group">
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 cursor-pointer flex items-center justify-between">
                      Test Inputs
                      <ChevronDownIcon className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                    </h3>
                  </Accordion.Trigger>
                  <Accordion.Content>
                    <div className="mb-8 mt-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                      <div className="grid grid-cols-2 gap-x-7 gap-y-4 items-start mt-2">
                        <div className="grid gap-6">
                          <div>
                            <label
                              htmlFor="sourceExample"
                              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                            >
                              Test Input
                            </label>
                            <div className="space-y-1 flex flex-col gap-1 mt-1">
                              <DragDrop
                                allowedTypes={['object/insert']}
                                onDrop={(data: Record<string, unknown>) =>
                                  handleDrop(
                                    data as { value: ObjectParams[] },
                                    'sourceExample'
                                  )
                                }
                                className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-500 border-dashed justify-center text-center h-5 bg-white dark:bg-gray-800"
                                droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-500 border-dashed bg-purple-50 dark:bg-purple-900/50 flex justify-center items-center h-5"
                              >
                                {({ isDropping }) =>
                                  isDropping ? (
                                    <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                                  ) : (
                                    <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                                  )
                                }
                              </DragDrop>
                              {sourceExample?.uuid &&
                              sourceExample?.container ? (
                                <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-2 pr-3 shadow-sm text-xs">
                                  <div className="relative w-10 h-10 bg-purple-50 dark:bg-purple-900/50 rounded-full flex justify-center items-center text-center">
                                    <LinkIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium text-gray-900 dark:text-gray-100">
                                      {sourceExample.container}
                                    </p>
                                    <p className="truncate text-gray-500 dark:text-gray-400">
                                      {sourceExample.uuid}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setValue('sourceExample', {
                                        uuid: '',
                                        container: '',
                                      });
                                      setValue(
                                        'inputObject',
                                        JSON.stringify({})
                                      );
                                      setJsonEditorData({});
                                      onChange(
                                        {
                                          ...defaultValues,
                                          sourceExample: {
                                            uuid: '',
                                            container: '',
                                          },
                                          inputObject: JSON.stringify({}),
                                        },
                                        'test'
                                      );
                                    }}
                                    className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : null}
                              <input
                                type="hidden"
                                {...register('sourceExample')}
                              />
                              {errors.sourceExample && (
                                <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                                  Test Input is required.
                                </p>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                              Vault Items
                            </span>
                            <div className="flex flex-col gap-2">
                              {(agentData?.vaultSchemas || [])
                                .filter((s: { uuid: string; variableName?: string }) => s.variableName && !s.variableName.includes('.'))
                                .map((schema: { uuid: string; variableName?: string }) => {
                                  const varName = schema.variableName!;
                                  const item = (vaultSchemas || []).find(
                                    (i) => i.variableName === varName
                                  );
                                  return (
                                    <div key={varName}>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{formatVarName(varName)}</p>
                                      {item ? (
                                        <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-2 pr-3 shadow-sm text-xs">
                                          <div className="relative w-10 h-10 bg-purple-50 dark:bg-purple-900/50 rounded-full flex justify-center items-center text-center">
                                            <LinkIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
                                          </div>
                                          <div className="min-w-0 flex-1">
                                            <p className="font-medium text-gray-900 dark:text-gray-100">
                                              {item.label || item.container}
                                            </p>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newItems = (vaultSchemas || []).filter(
                                                (i) => i.variableName !== varName
                                              );
                                              setValue('vaultSchemas', newItems);
                                              onChange({ ...defaultValues, vaultSchemas: newItems }, 'test');
                                            }}
                                            className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ) : (
                                        <Popover.Root
                                          open={openVaultPopover === varName}
                                          onOpenChange={(open) => setOpenVaultPopover(open ? varName : null)}
                                        >
                                          <Popover.Anchor asChild>
                                            <div onClick={() => setOpenVaultPopover(varName)}>
                                              <DragDrop
                                                allowedTypes={['object/insert']}
                                                onDrop={(data: Record<string, unknown>) => handleVaultDrop(data, varName)}
                                                className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-500 border-dashed justify-center text-center h-5 bg-white dark:bg-gray-800 cursor-pointer"
                                                droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-500 border-dashed bg-purple-50 dark:bg-purple-900/50 flex justify-center items-center h-5"
                                              >
                                                {({ isDropping }) =>
                                                  isDropping ? (
                                                    <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                                                  ) : (
                                                    <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                                                  )
                                                }
                                              </DragDrop>
                                            </div>
                                          </Popover.Anchor>
                                          <Popover.Portal>
                                            <Popover.Content
                                              className="relative bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                                              sideOffset={5}
                                            >
                                              <ObjectPickerForm
                                                type="vault"
                                                options={vaultPickerItems
                                                  .filter((i) => i.data?.dataClassUuid === schema.uuid)
                                                  .map((i) => ({ value: i.uuid, label: i.label }))}
                                                onCreateNew={() => window.open(`/vault?dataClassUuid=${schema.uuid}`, '_blank')}
                                                onSubmit={(option) => {
                                                  if (!option) return;
                                                  handleVaultDrop(
                                                    { value: [{ uuid: option.value, container: 'vault', label: option.label }] },
                                                    varName
                                                  );
                                                  setOpenVaultPopover(null);
                                                }}
                                              />
                                              <button
                                                type="button"
                                                onClick={() => queryClient.invalidateQueries({ queryKey: ['ui-picker-objects', 'vault'] })}
                                                className="absolute top-2 right-8 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                                title="Refresh vault items"
                                              >
                                                <ArrowPathIcon className={`h-4 w-4${isVaultRefetching ? ' animate-spin' : ''}`} />
                                              </button>
                                              <Popover.Close className="absolute top-2 right-2" aria-label="Close">
                                                <XMarkIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                              </Popover.Close>
                                              <Popover.Arrow asChild style={{ filter: 'drop-shadow(0 -3px 6px rgba(0,0,0,0.08))' }}>
                                                <svg width="10" height="5" viewBox="0 0 10 5">
                                                  <polygon points="0,0 5,5 10,0" className="fill-white dark:fill-gray-800" />
                                                  <polyline points="0,0 5,5 10,0" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" fill="none" />
                                                </svg>
                                              </Popover.Arrow>
                                            </Popover.Content>
                                          </Popover.Portal>
                                        </Popover.Root>
                                      )}
                                    </div>
                                  );
                                })}
                              <input type="hidden" {...register('vaultSchemas')} />
                            </div>
                          </div>
                        </div>
                        <div className="pt-7">
                          <JsonEditor
                            data={jsonEditorData}
                            onUpdate={(data) => {
                              setJsonEditorData(data);
                              setValue('inputObject', JSON.stringify(data));
                              onChange(
                                {
                                  ...defaultValues?.test,
                                  inputObject: JSON.stringify(data),
                                },
                                'test'
                              );
                            }}
                          />
                          <input
                            id="inputObject"
                            type="hidden"
                            {...register('inputObject')}
                          />
                        </div>
                      </div>
                    </div>
                  </Accordion.Content>
                </div>
              </form>
            </Accordion.Item>
            <Accordion.Item value="test-results">
              <div className=" p-4 rounded-lg bg-gray-50 dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700/50">
                <Accordion.Trigger asChild className="group">
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 cursor-pointer flex items-center justify-between">
                    Test Results
                    <ChevronDownIcon className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                  </h3>
                </Accordion.Trigger>
                <Accordion.Content>
                  <div className="grid grid-cols-2 gap-4  mt-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="row-span-2">
                      <div className="flex gap-x-4 items-center">
                        {runtimeData?.status === 'running' || testAgentMutation.isLoading ? (
                          <Badge variant="success">Running</Badge>
                        ) : runtimeData?.status === 'failed' ? (
                          <Badge variant="error">Failed</Badge>
                        ) : runtimeData?.status === 'cancelled' ? (
                          <Badge variant="warning">Cancelled</Badge>
                        ) : (
                          <Badge variant="info">Inactive</Badge>
                        )}
                        {startTime ? (
                          <>
                            <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto inline-flex items-center gap-x-1">
                              <ClockIcon className="h-4 w-4" />
                              Started at{' '}
                              <span className="font-mono text-xs">
                                {startTime.toLocaleTimeString()}
                              </span>
                            </span>
                            <Timer startTime={startTime} />
                          </>
                        ) : null}
                      </div>
                      <ReactECharts
                        option={options}
                        style={{ width: '100%' }}
                        notMerge={false}
                      />
                    </div>
                    <Editor
                      value={
                        runtimeData?.output
                          ? JSON.stringify(runtimeData.output, null, 2)
                          : ''
                      }
                      defaultLanguage="json"
                      height={350}
                      className="rounded-md border-gray-300 dark:border-gray-600 border overflow-hidden py-2 pr-2 bg-[#fffffe] dark:bg-gray-800"
                      readOnly
                      allowFullscreen
                    />
                    <div className="col-start-2 grid gap-y-4 grid-cols-2 gap-4">
                      <Output title="Log">{runtimeData?.log}</Output>
                      <div className="flex flex-col gap-y-2">
                        <Output title="Error">{runtimeData?.error}</Output>
                        <div>
                          <Button
                            variant="tertiary"
                            onClick={handleAutoCorrect}
                            loading={refineAgentMutation.isLoading}
                          >
                            Auto correct
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Accordion.Content>
              </div>
            </Accordion.Item>
            <Accordion.Item value="create-automated-tests">
              <CreateAutomatedTests
                defaultValues={defaultValues}
                onChange={onChange}
              />
            </Accordion.Item>
            {isAuthorised(['developer', 'fe.agentstudio.developer']) ? (
              <Accordion.Item value="publish">
                <div className=" p-4 rounded-lg bg-gray-50 dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700/50">
                  <Accordion.Trigger asChild className="group">
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 cursor-pointer flex items-center justify-between">
                      Publish
                      <ChevronDownIcon className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                    </h3>
                  </Accordion.Trigger>
                  <Accordion.Content className="mt-4">
                    <button
                      className="rounded-md bg-indigo-600 dark:bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 dark:hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handlePublish}
                    >
                      Publish
                    </button>
                  </Accordion.Content>
                </div>
              </Accordion.Item>
            ) : null}
            <Accordion.Item value="initialize">
              <div className=" p-4 rounded-lg bg-gray-50 dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700/50">
                <Accordion.Trigger asChild className="group">
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 cursor-pointer flex items-center justify-between">
                    Initialize
                    <ChevronDownIcon className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                  </h3>
                </Accordion.Trigger>
                <Accordion.Content className="mt-4">
                  <Button
                    onClick={handleInitialize}
                    type="submit"
                    variant="tertiary"
                    loading={initializeAgentMutation.isLoading}
                  >
                    Initialize
                  </Button>
                </Accordion.Content>
              </div>
            </Accordion.Item>
          </Accordion.Root>
        </div>
      )}
    </>
  );
}

interface CreateAutomatedTestsInputs {
  testInput?: string;
  testOutput?: string;
}

function CreateAutomatedTests({
  defaultValues,
  onChange,
}: {
  onChange: (
    data: TestFormInputs | CreateAutomatedTestsInputs,
    step: 'test'
  ) => void;
  defaultValues: DefaultValues['test'];
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CreateAutomatedTestsInputs>({
    values: {
      testInput: defaultValues?.testInput,
      testOutput: defaultValues?.testOutput,
    },
  });
  const createAutomatedTestsMutation = useRunAgentTest();
  const [result, setResult] = useState<{
    status: 'success' | 'error';
    message: string;
  } | null>(null);
  const [jsonEditorData, setJsonEditorData] = useState<Record<string, unknown>>(
    {}
  );

  function onRun(data: CreateAutomatedTestsInputs) {
    setResult(null);

    createAutomatedTestsMutation.mutate(
      {
        testInput: data.testInput || '',
        testOutput: data.testOutput || '',
      },
      {
        onSuccess: (data) => {
          setResult({
            status: data.status || 'error',
            message: data.message || 'Test creation failed',
          });
        },
      }
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onRun)}
      className=" p-4 rounded-lg bg-gray-50 dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700/50"
    >
      <Accordion.Trigger asChild className="group">
        <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 cursor-pointer flex items-center justify-between">
          Create Automated Tests
          <ChevronDownIcon className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
        </h3>
      </Accordion.Trigger>
      <Accordion.Content>
        <div className="grid grid-cols-2 gap-y-4 gap-x-7 mt-4 border-t border-gray-200 dark:border-gray-700 pt-6">
          <div className="flex flex-col">
            <label
              htmlFor="tune"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
            >
              Describe test input scenario
            </label>
            <PromptBuilder
              className="mt-2"
              content={JSON.parse(defaultValues?.testInput || '{}')}
              onChange={(value) => {
                setValue('testInput', value);
                onChange(
                  {
                    ...defaultValues,
                    testInput: value,
                  },
                  'test'
                );
              }}
            />
            <input
              id="testInput"
              type="hidden"
              {...register('testInput', {
                required: true,
              })}
            />
            {errors.testInput && (
              <p className="text-red-500 text-sm mt-1">
                Input scenario is required.
              </p>
            )}
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="tune"
              className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
            >
              Describe expected test outcome
            </label>
            <JsonEditor
              className="mt-2 h-full"
              data={jsonEditorData}
              onUpdate={(data) => {
                setJsonEditorData(data);
                setValue('testOutput', JSON.stringify(data));
                onChange(
                  {
                    ...defaultValues?.test,
                    testOutput: JSON.stringify(data),
                  },
                  'test'
                );
              }}
            />
            <input
              id="testOutput"
              type="hidden"
              {...register('testOutput', {
                required: true,
              })}
            />
            {errors.testOutput && (
              <p className="text-red-500 text-sm mt-1">
                Expected test outcome is required.
              </p>
            )}
          </div>
          <div className="col-span-2 flex gap-x-4 justify-end">
            <button
              type="submit"
              className="rounded-md bg-indigo-600 dark:bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 dark:hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Run Tests
            </button>
          </div>
          <div className="col-span-2">
            {result ? (
              <Result status={result.status} message={result.message} />
            ) : null}
          </div>
        </div>
      </Accordion.Content>
    </form>
  );
}

function Result({
  status,
  message,
}: {
  status: 'success' | 'error';
  message: string;
}) {
  return (
    <div className="px-4 pt-4 pb-3 rounded-lg bg-gray-50 dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 dark:ring-gray-700/50">
      <h3 className="sr-only">Test Results</h3>
      <div>
        <div
          className={clsx(
            'inline-flex items-center gap-x-1 rounded-full pl-1.5 pr-2.5 py-0.5 text-sm font-medium md:mt-2 lg:mt-0',
            status === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          )}
        >
          {status === 'success' ? (
            <>
              <CheckCircleIcon className="h-4 w-4" />
              <span>Success</span>
            </>
          ) : (
            <>
              <XCircleIcon className="h-4 w-4" />
              <span>Error</span>
            </>
          )}
        </div>
        <p className="text-sm text-gray-900 dark:text-gray-200 mt-2">
          {message}
        </p>
      </div>
    </div>
  );
}
